/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import AutoFixHighOutlined from '@mui/icons-material/AutoFixHighOutlined';
import CrisisAlertOutlined from '@mui/icons-material/CrisisAlertOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import {
  Avatar,
  Box,
  ClickAwayListener,
  Fade,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  type Theme,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { AppSystemPermissions } from '@poweralpha/common';
import type React from 'react';
import { type Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { usePermissions } from '../auth';
import { useLocalStorage } from './useLocalStorage';

const avatarSx = (theme: Theme) => ({
  bgcolor: theme.palette.secondary.light,
  color:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.primary.main,
});

const paperSx = {
  mt: 1,
  mr: 1,
  zIndex: 5,
  width: 220,
  borderRadius: 1,
};

interface Props {
  userName: string;
  prefersDarkMode: boolean;
  onLogout: () => void;
  onThemeChange: Dispatch<React.SetStateAction<'dark' | 'light'>>;
}

export const UserMenu = ({
  userName,
  prefersDarkMode,
  onLogout,
  onThemeChange,
}: Readonly<Props>) => {
  const anchorEl = useRef<HTMLButtonElement>(null);
  const permissions = usePermissions();

  const [open, setOpen] = useState(false);
  const [themeMode, setThemeMode] = useLocalStorage<'dark' | 'light' | 'automatic'>(
    'theme-mode',
    'automatic',
  );

  const handleClick = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const handleThemeMode = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: string | null) => {
      if (!newMode) return;
      setThemeMode(newMode as 'dark' | 'light' | 'automatic');
    },
    [setThemeMode],
  );

  useEffect(() => {
    switch (themeMode) {
      case 'automatic':
        onThemeChange(prefersDarkMode ? 'dark' : 'light');
        break;
      default:
        onThemeChange(themeMode);
    }
  }, [onThemeChange, prefersDarkMode, themeMode]);

  const showSystemAlerts = permissions?.includes(AppSystemPermissions.SYSTEM_ADMIN) ?? false;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box>
        <IconButton
          aria-label="user-menu"
          id="user-button"
          ref={anchorEl}
          aria-controls={open ? 'user-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="true"
          size="small"
          onClick={handleClick}
        >
          <Avatar
            sx={avatarSx}
          >{`${userName.split(' ')[0][0]}${userName.split(' ')[1][0]}`}</Avatar>
        </IconButton>
        <Popper
          id={open && Boolean(anchorEl) ? 'transition-popper' : undefined}
          open={open}
          anchorEl={anchorEl.current}
          transition
          placement="bottom-end"
          onClick={(e) => e.stopPropagation()}
          sx={{ zIndex: 2000 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper elevation={3} sx={paperSx}>
                <Stack>
                  <Typography
                    sx={{
                      p: 1,
                      textAlign: 'end',
                      bgcolor: 'background.paper',
                    }}
                  >
                    {userName}
                  </Typography>
                  {showSystemAlerts && (
                    <ListItemButton
                      component={NavLink}
                      to="/admin/system-issues"
                      onClick={handleClose}
                      sx={{
                        mx: 0.5,
                        borderRadius: 1,
                        minHeight: 40,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <CrisisAlertOutlined fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="System Alerts" />
                    </ListItemButton>
                  )}
                  <Box
                    sx={{
                      height: 40,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 0.5,
                        pr: 1,
                        pl: 1,
                      }}
                    >
                      <ToggleButtonGroup
                        value={themeMode}
                        onChange={handleThemeMode}
                        exclusive
                        size="small"
                        sx={{ height: 30 }}
                      >
                        <Tooltip title="Light mode">
                          <ToggleButton value="light">
                            <LightModeOutlined sx={{ height: 20 }} />
                          </ToggleButton>
                        </Tooltip>
                        <Tooltip title="Dark mode">
                          <ToggleButton value="dark">
                            <DarkModeOutlined sx={{ height: 20 }} />
                          </ToggleButton>
                        </Tooltip>
                        <Tooltip title="Set automatically based on OS">
                          <ToggleButton value="automatic">
                            <AutoFixHighOutlined sx={{ height: 20 }} />
                          </ToggleButton>
                        </Tooltip>
                      </ToggleButtonGroup>
                      <Typography
                        onClick={onLogout}
                        sx={{ color: 'error.main', cursor: 'pointer' }}
                      >
                        <u>Logout</u>
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
